import { Icon } from '@natera/material/lib/icon'
import { Collapsible } from 'radix-ui'
import { centsToDollars } from '~/shared/lib/currency'
import { CollapsibleContent, Text } from '~/shared/ui'

import type { AmountType } from '../../../../model'
import { CarcRarcGroupForm } from './carc-rarc-group-form'
import type { DeductionField, GroupState, UnifiedGroup } from './carc-rarc-group-types'
import { isGroupValid } from './carc-rarc-group-types'

interface CarcRarcCollapsibleGroupProps {
  index: number
  group: UnifiedGroup
  deductionField: DeductionField
  amountType: AmountType
  onStateChange: (state: GroupState) => void
  onDelete: () => void
  onToggleCollapse: () => void
}

function formatAmount(value: number | null, amountType: AmountType): string | null {
  if (value === null) return null
  if (amountType === 'percentage') return `${value}%`
  const dollars = centsToDollars(value)
  if (dollars === null) return null
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function CarcRarcCollapsibleGroup({
  index,
  group,
  deductionField,
  amountType,
  onStateChange,
  onDelete,
  onToggleCollapse,
}: CarcRarcCollapsibleGroupProps) {
  const valid = isGroupValid(group.state)
  const { selectedPrefix, selectedCarcCodes, selectedRarcCodes, amount } = group.state

  const fullCarcs = selectedCarcCodes.map((c) => `${selectedPrefix ?? ''}${c}`)
  const amountLabel = formatAmount(amount, amountType)

  return (
    <Collapsible.Root
      open={!group.collapsed}
      onOpenChange={() => valid && onToggleCollapse()}
    >
      {/* Collapsed view — summary with caret */}
      {group.collapsed && valid && (
        <Collapsible.Trigger asChild>
          <button
            className="border-divider bg-surface-low flex w-full cursor-pointer items-center justify-between rounded-lg border px-4 py-3"
            type="button"
          >
            <div className="flex flex-col gap-1">
              <Text appearance="BODY2">
                <span className="text-text-secondary">CARC:</span> {fullCarcs.join(', ')}
              </Text>
              {selectedRarcCodes.length > 0 && (
                <Text appearance="BODY2">
                  <span className="text-text-secondary">RARCs:</span> {selectedRarcCodes.join(', ')}
                </Text>
              )}
              {amountLabel && (
                <Text appearance="BODY2">
                  <span className="text-text-secondary">Amount:</span> {amountLabel}
                </Text>
              )}
            </div>
            <Icon
              className="text-action-active self-center"
              children="keyboard_arrow_down"
            />
          </button>
        </Collapsible.Trigger>
      )}

      {/* Expanded header trigger */}
      {!group.collapsed && (
        <Collapsible.Trigger asChild>
          <button
            className="bg-surface-low border-divider flex h-10 w-full cursor-pointer items-center justify-between rounded-t-lg border px-4"
            disabled={!valid}
            type="button"
          >
            <Text appearance="BODY2">Code Group {index + 1}</Text>
            {valid && (
              <Icon
                className="text-action-active"
                children="keyboard_arrow_up"
              />
            )}
          </button>
        </Collapsible.Trigger>
      )}

      {/* Expanded form */}
      <CollapsibleContent>
        <div className="border-divider rounded-b-lg border border-t-0 px-4 py-3">
          <CarcRarcGroupForm
            group={group.state}
            deductionField={deductionField}
            amountType={amountType}
            onChange={onStateChange}
            onDelete={onDelete}
          />
        </div>
      </CollapsibleContent>
    </Collapsible.Root>
  )
}
